/* ============================================================================
   User Priorities Map, the single source of truth (Chunk B)
   ----------------------------------------------------------------------------
   One priorities object captured by the Nex companion questionnaire on first run,
   editable any time from the Profile sheet. It powers four product surfaces:

     1. Custom Dexes        what to track beyond Main/Shiny/Lucky/Perfect
     2. Storage Optimiser   personalised default protection profile
     3. Feature elevation   which zone tiles are surfaced more prominently
     4. Friend matching     (Chunk C) which friends match the user's gaps

   Keep all derivation pure here so the UI stays declarative and we can
   evolve the questionnaire without touching downstream consumers.
============================================================================ */

import type { ProtectionProfile, KeepRule } from "@/components/SessionProvider";

// ── Primitives ────────────────────────────────────────────────────────────

// Primary play styles. Order matters in the UI but is not semantic.
export type PlayStyle = "pvp" | "raids" | "collector" | "trader" | "casual";

// Collection foci. Multi-select. Order in the array reflects user priority,
// first item = highest priority.
//
// v3 (2026-08-25 ask): the questionnaire's tiles are now the eight foci that
// map 1:1 onto dexes the app actually tracks — gender_pair / variants /
// costumes / pvp_top were replaced by dynamax / xxs / xxl / shiny_lucky.
// The retired values STAY in the union: saved priority blobs still carry
// them until the trainer re-saves the questionnaire (which migrates them
// out), and every downstream surface keeps handling them gracefully.
export type CollectionFocus =
  | "shiny_dex"       // any shiny, regardless of IV / lucky / gender
  | "shundo"          // shiny + hundo, the unicorn
  | "hundos"          // 100% IV (4-star perfect), any form
  | "lucky"           // lucky pokemon (half stardust to power up)
  | "dynamax"         // Max Battle catches (curated Dynamax dex)
  | "xxs"             // record-small sizes
  | "xxl"             // record-large sizes
  | "shiny_lucky"     // shiny AND lucky, the trade unicorn
  // Retired tiles (v2) — read-only compat for existing saved blobs:
  | "gender_pair"     // male AND female of dimorphic species
  | "variants"        // regionals, alt forms, Vivillon, etc.
  | "costumes"        // hats, scarves, party
  | "pvp_top";        // top 10 IVs in the user's chosen leagues

// Sub-mode for the gender_pair focus. Decides whether the M/F pair tile
// tracks shiny pairs only or any-gender pairs of dimorphic species.
export type GenderPairMode = "shiny" | "any";

// PvP league focus. Multi-select. Order reflects priority.
export type LeagueFocus =
  | "great"
  | "ultra"
  | "master"
  | "limited_meta"   // theme cups, restricted formats
  | "none";

export type DustBudget = "tight" | "moderate" | "abundant";
export type BagPressure = "always_full" | "sometimes" | "rarely";
export type TradeFrequency = "daily" | "weekly" | "rarely";

// ── The Priorities object ─────────────────────────────────────────────────

export interface UserPriorities {
  primaryStyle: PlayStyle;
  secondaryStyle: PlayStyle | null;
  collectionFoci: CollectionFocus[];
  // Sub-mode for the gender_pair focus tile (only meaningful when
  // collectionFoci includes "gender_pair"). Defaults to "shiny".
  genderPairMode: GenderPairMode;
  leagueFoci: LeagueFocus[];
  dustBudget: DustBudget;
  bagPressure: BagPressure;
  tradeFrequency: TradeFrequency;
  /**
   * Pro: up to three friends the trainer has pinned as their inner circle
   * (see lib/priorityFriends). Their leads are surfaced first on the Profile
   * page. Not a questionnaire answer — it rides in this object because
   * priorities is the account's already-synced preference blob (the server
   * schema is `.passthrough()`), so pinned friends persist across devices with
   * no extra table. Older accounts have no key at all, which is why every read
   * goes through `priorityFriendIds()` rather than touching the field directly.
   */
  priorityFriendIds?: number[];
  // Null when the user hasn't completed (or has skipped) the questionnaire.
  // We still ship sensible defaults so the app works without it.
  questionnaireCompletedAt: string | null;
  // Bump this when we add or change questions to know if the saved answers
  // need a refresh prompt.
  questionnaireVersion: number;
}

export const CURRENT_QUESTIONNAIRE_VERSION = 3;

// Defaults assume a balanced "collector who also dabbles in PvP" player.
// These keep the rest of the app behaving sensibly before the questionnaire
// is taken or if the user skips it entirely.
export const DEFAULT_USER_PRIORITIES: UserPriorities = {
  primaryStyle: "collector",
  secondaryStyle: "pvp",
  collectionFoci: ["shiny_dex", "hundos"],
  genderPairMode: "shiny",
  leagueFoci: ["great", "ultra"],
  dustBudget: "moderate",
  bagPressure: "sometimes",
  tradeFrequency: "weekly",
  priorityFriendIds: [],
  questionnaireCompletedAt: null,
  questionnaireVersion: CURRENT_QUESTIONNAIRE_VERSION,
};

/*
 * The Custom Dex model stood here until 2026-08-14: a named filter kind
 * ("shundo", "shiny_male", "league_top", "custom") that a trainer could build a
 * dex out of. It was removed with the builder that made them and the invented
 * "top 50 meta" lists that filled them. Nobody was using freestyle dexes, and
 * the three custom dexes that ARE used (Dynamax, Shiny Lucky, Shiny Perfect)
 * are a different mechanism entirely: real PokedexVariants in dexStateStore,
 * with their own add sheets and cascade rules. This removal does not touch
 * them. See client/src/pages/BattleHub.tsx for what replaced the meta half.
 */

// ── Derivation, priorities → protection profile ───────────────────────────


// ── Derivation, priorities → built-in custom dexes ────────────────────────

let _seq = 0;
function nextId(prefix: string): string {
  _seq += 1;
  return `${prefix}-${_seq.toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

// Build the seeded list of custom dexes. We always include the four "trophy"
// dexes (Shundo, Shiny Lucky, Shiny Male, Shiny Female), plus dexes that
// match the user's collection foci, plus one league_top per leagueFoci entry.

// ── Derivation, priorities → feature elevation weights ────────────────────

// Each zone gets a 0-1 weight. UI surfaces (Profile tiles, navigation, Today's
// Focus) can use these to elevate or de-emphasise sections without hiding
// anything. 1 = strongly emphasise, 0 = de-emphasise.
export interface FeatureWeights {
  connect: number;   // friends, trade tools
  catch: number;     // collection completion
  coach: number;     // 6v6 battle, ROI
  storage: number;   // Storage Optimiser
}

export function deriveFeatureWeights(priorities: UserPriorities): FeatureWeights {
  const styles = [priorities.primaryStyle, priorities.secondaryStyle].filter(
    (s): s is PlayStyle => s !== null,
  );
  const has = (s: PlayStyle) => styles.includes(s);

  // Base weights by style.
  let connect = 0.4;
  let catchW = 0.4;
  let coach = 0.4;
  let storage = 0.4;

  if (has("pvp")) coach += 0.4;
  if (has("raids")) coach += 0.25;
  if (has("collector")) catchW += 0.4;
  if (has("trader")) connect += 0.4;
  if (has("casual")) {
    // Casuals get a softer profile, slightly elevate Catch (the easy zone)
    // and de-emphasise Coach.
    catchW += 0.15;
    coach -= 0.1;
  }

  // Storage scales with bag pressure.
  if (priorities.bagPressure === "always_full") storage += 0.4;
  else if (priorities.bagPressure === "sometimes") storage += 0.15;

  // Trade frequency lifts Connect.
  if (priorities.tradeFrequency === "daily") connect += 0.2;

  // Collection foci lift Catch.
  if (priorities.collectionFoci.length >= 3) catchW += 0.1;

  // Multi-league PvP lifts Coach further.
  if (priorities.leagueFoci.filter((l) => l !== "none").length >= 2) coach += 0.15;

  function clamp(n: number) { return Math.max(0, Math.min(1, n)); }
  return { connect: clamp(connect), catch: clamp(catchW), coach: clamp(coach), storage: clamp(storage) };
}

// Convenience: returns the zones in elevation order, highest weight first.
// Used by Profile to reorder the zone tiles.
export type ZoneKey = keyof FeatureWeights;

// ── Pretty labels ─────────────────────────────────────────────────────────

export function playStyleLabel(s: PlayStyle): string {
  switch (s) {
    case "pvp": return "PvP battler";
    case "raids": return "Raid hunter";
    case "collector": return "Collector";
    case "trader": return "Trader";
    case "casual": return "Casual";
  }
}

export function collectionFocusLabel(f: CollectionFocus): string {
  switch (f) {
    case "shiny_dex": return "Shiny Dex";
    case "shundo": return "Shundos";
    case "hundos": return "Hundos";
    case "lucky": return "Lucky";
    case "dynamax": return "Dynamax";
    case "xxs": return "XXS";
    case "xxl": return "XXL";
    case "shiny_lucky": return "Shiny Lucky";
    case "gender_pair": return "Male / Female";
    case "variants": return "Variants";
    case "costumes": return "Costumes";
    case "pvp_top": return "PvP Top 10";
    default: {
      // A focus this build has no case for. Priorities are shared across a
      // trainer's devices, so a newer app can save a key an older one has never
      // heard of: on 2026-09-29 an iOS build from before 2026-08-25 met
      // "dynamax" / "xxs" / "xxl" (added that day), the switch fell through to
      // undefined, and Profile crashed on label.endsWith (error_log 909-911).
      // Hand back a readable label instead of a hole for the caller to trip on.
      const raw: string = f;
      return raw
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }
  }
}

// b15b U3: map a collection focus to the Pokédex variant whose device-local
// dex-state drives the priority "trophy dex" panel on Profile. Every focus
// with a clean 1:1 variant the dex store tracks returns that variant
// (2026-08-25 ask: dynamax / xxs / xxl / shiny_lucky link their dexes to
// the Profile page on selection); hundos/shundo keep the canonical rich
// Hundo-dex panel (it carries IV + source + shiny/lucky badges the local
// store can't), and the retired multi-tile foci (gender_pair, variants,
// costumes, pvp_top) return null so they fall back to the Hundo panel.
export type FocusDexVariant =
  | "shiny"
  | "lucky"
  | "dynamax"
  | "xxs"
  | "xxl"
  | "shinyLucky";

export function focusToDexVariant(f: CollectionFocus): FocusDexVariant | null {
  switch (f) {
    case "shiny_dex": return "shiny";
    case "lucky": return "lucky";
    case "dynamax": return "dynamax";
    case "xxs": return "xxs";
    case "xxl": return "xxl";
    case "shiny_lucky": return "shinyLucky";
    default: return null;
  }
}

// b18-profile-1: returns false for foci where the trophy slot should be hidden
// entirely (variants, gender_pair, costumes have no dex-count surface yet).
// Returns true for every other CollectionFocus value, including the fallback
// path where no questionnaire has been completed (top focus is undefined).
export function shouldRenderTrophySlot(
  focus: CollectionFocus | undefined,
): boolean {
  if (!focus) return true;
  switch (focus) {
    case "variants":
    case "gender_pair":
    case "costumes":
      return false;
    default:
      return true;
  }
}

export function leagueFocusLabel(l: LeagueFocus): string {
  switch (l) {
    case "great": return "Great League";
    case "ultra": return "Ultra League";
    case "master": return "Master League";
    case "limited_meta": return "Limited Meta";
    case "none": return "Not into PvP";
  }
}

